import React, { createContext, useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import useAuth from '../hooks/useAuth';
import {
  getGradingPolicies,
  createGradingPolicy,
  updateGradingPolicy,
  deleteGradingPolicy,
  getGradingPolicyById,
  getGradingPolicyByName,
} from '../services/gradingPolicyService';
import Notification from '../components/Common/Notification';

export const GradingPolicyContext = createContext();

export const GradingPolicyProvider = ({ children }) => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [gradingPolicies, setGradingPolicies] = useState([]);
  const [selectedGradingPolicy, setSelectedGradingPolicy] = useState(null);
  const [notification, setNotification] = useState({ message: '', isError: false });

  const fetchAuthHeaders = () => {
    const storedUser = localStorage.getItem('user');
    const token = user?.token || (storedUser ? JSON.parse(storedUser).token : '');

    if (!token) {
      console.error('User or token not available');
      return {};
    }

    return {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
  };

  const showNotification = (message, isError) => {
    setNotification({ message, isError });
  };

  const closeNotification = () => {
    setNotification({ message: '', isError: false });
  };

  const fetchAllGradingPolicies = async () => {
    try {
      const authHeaders = fetchAuthHeaders();
      const data = await getGradingPolicies(authHeaders);
      setGradingPolicies(data);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || 'Error fetching grading policies.';
      showNotification(errorMessage, true);
    }
  };

  const fetchGradingPolicyById = async (id) => {
    try {
      const authHeaders = fetchAuthHeaders();
      const data = await getGradingPolicyById(id, authHeaders);
      setSelectedGradingPolicy(data);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || `Error fetching grading policy with ID ${id}.`;
      showNotification(errorMessage, true);
    }
  };

  const fetchGradingPolicyByName = async (policyName) => {
    try {
      const authHeaders = fetchAuthHeaders();
      const data = await getGradingPolicyByName(policyName, authHeaders);
      setSelectedGradingPolicy(data);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || `Error fetching grading policy with name ${policyName}.`;
      showNotification(errorMessage, true);
    }
  };

  const addGradingPolicy = async (gradingPolicyData) => {
    try {
      const authHeaders = fetchAuthHeaders();
      const newGradingPolicy = await createGradingPolicy(gradingPolicyData, authHeaders);
      setGradingPolicies(prevPolicies => [...prevPolicies, newGradingPolicy]);
      showNotification('Grading policy added successfully!', false);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || 'Error adding grading policy.';
      showNotification(errorMessage, true);
    }
  };

  const editGradingPolicy = async (id, gradingPolicyData) => {
    try {
      const authHeaders = fetchAuthHeaders();
      const updatedGradingPolicy = await updateGradingPolicy(id, gradingPolicyData, authHeaders);
      setGradingPolicies(prevPolicies =>
        prevPolicies.map(policy => (policy.id === id ? updatedGradingPolicy : policy))
      );
      showNotification('Grading policy updated successfully!', false);
      return updatedGradingPolicy;
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || 'Error updating grading policy.';
      showNotification(errorMessage, true);
      throw error;
    }
  };

  const removeGradingPolicy = async (id) => {
    try {
      const authHeaders = fetchAuthHeaders();
      await deleteGradingPolicy(id, authHeaders);
      setGradingPolicies(prevPolicies => prevPolicies.filter(policy => policy.id !== id));
      showNotification('Grading policy removed successfully!', false);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || 'Error removing grading policy.';
      showNotification(errorMessage, true);
    }
  };

  // useEffect(() => {
  //   fetchAllGradingPolicies();
  // }, []);


  useEffect(() => {
    if (!isAuthenticated && !['/', '/login'].includes(window.location.pathname)) {
      navigate('/login');
    }
  }, [isAuthenticated, navigate]);


  return (
    <GradingPolicyContext.Provider
      value={{
        gradingPolicies,
        selectedGradingPolicy,
        fetchAllGradingPolicies,
        fetchGradingPolicyById,
        fetchGradingPolicyByName,
        addGradingPolicy,
        editGradingPolicy,
        removeGradingPolicy,
      }}
    >
      {children}
      {notification.message && (
        <Notification
          message={notification.message}
          isError={notification.isError}
          onClose={closeNotification}
        />
      )}
    </GradingPolicyContext.Provider>
  );
};

export const useGradingPolicy = () => useContext(GradingPolicyContext);
